import React, { useEffect, useMemo } from 'react';
import {
    AttachmentKey,
    ConversationMessageKey,
} from 'flow/types/TicketEventData';
import { TicketKey } from 'flow/types/EntityKeys';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectEventNumberOfAttachments,
    selectEventTimestamp,
    selectTicketMessageAttachments,
} from 'flow/selectors/TicketEventSelectors';
import { fetchTicketMessage } from 'flow/actions/tickets-action';
import AttachmentBeingSentPlaceholder from './AttachmentBeingSentPlaceholder';
import TicketMessageAttachment from 'flow/components/tickets/event-item/Messages/TicketMessageAttachment';

interface Props {
    eventKey: ConversationMessageKey;
    ticketKey: TicketKey;
    onNewAttachmentsFetched?: () => void;
    isBeingSent?: boolean;
    attachmentsToExclude?: Set<AttachmentKey>;
}

const AttachmentsContainer = ({
    eventKey,
    ticketKey,
    onNewAttachmentsFetched,
    isBeingSent,
    attachmentsToExclude,
}: Props) => {
    const dispatch = useAppDispatch();
    const numberOfAttachmentsTotal = useAppSelector((state) =>
        selectEventNumberOfAttachments(state, ticketKey, eventKey),
    );
    const attachmentsInStore = useAppSelector((state) =>
        selectTicketMessageAttachments(state, ticketKey, eventKey),
    );
    const sentTime = useAppSelector((state) =>
        selectEventTimestamp(state, ticketKey, eventKey),
    );
    const amountFetched = attachmentsInStore?.size || 0;

    useEffect(() => {
        if (isBeingSent) return;

        if (numberOfAttachmentsTotal && amountFetched === 0) {
            void dispatch(fetchTicketMessage(ticketKey, eventKey, true));
        }
    }, [
        dispatch,
        ticketKey,
        eventKey,
        numberOfAttachmentsTotal,
        amountFetched,
        onNewAttachmentsFetched,
        isBeingSent,
    ]);

    const attachmentsToDisplay = useMemo(() => {
        if (
            !attachmentsInStore ||
            !attachmentsToExclude ||
            attachmentsToExclude.size === 0
        ) {
            return attachmentsInStore;
        }
        return attachmentsInStore.filter(
            (item) => !attachmentsToExclude.has(item.get('key')),
        );
    }, [attachmentsInStore, attachmentsToExclude]);

    const numberOfAttachmentsToDisplay = attachmentsToDisplay?.size || 0;
    const prevNumberOfAttachmentsRef = React.useRef<number>(
        numberOfAttachmentsToDisplay,
    );

    useEffect(() => {
        if (numberOfAttachmentsToDisplay > prevNumberOfAttachmentsRef.current) {
            onNewAttachmentsFetched?.();
        }
        prevNumberOfAttachmentsRef.current = numberOfAttachmentsToDisplay;
    }, [numberOfAttachmentsToDisplay, onNewAttachmentsFetched]);

    if (!numberOfAttachmentsToDisplay && !attachmentsToDisplay) return null;
    if (numberOfAttachmentsToDisplay && !attachmentsToDisplay) {
        return (
            <>
                {Array.from({ length: numberOfAttachmentsToDisplay }).map(
                    (_, index) => (
                        <div
                            key={`acpl-${eventKey}-${index}`}
                            className="h-[200px]"
                        />
                    ),
                )}
            </>
        );
    }
    if (attachmentsToDisplay && isBeingSent) {
        return (
            <>
                {attachmentsToDisplay.map((item) => (
                    <AttachmentBeingSentPlaceholder
                        key={`absp-${item.get('key')}`}
                        fileType={item.get('fileType')}
                        aspectRatio={item.get('aspectRatio')}
                    />
                ))}
            </>
        );
    }

    return (
        <>
            {attachmentsToDisplay?.map((item) => (
                <TicketMessageAttachment
                    key={`ma-${item.get('key')}`}
                    attachment={item}
                    sentTime={sentTime}
                />
            ))}
        </>
    );
};

export default React.memo(AttachmentsContainer);
